import {
  EventManyQuery,
  useEventByIdPartialDetailsLazyQuery,
  EventWithOccurrences,
  EventByIdPartialDetailsQuery,
} from "gql/generated";
import { useEffect, useState } from "react";

// Cache the responses from the API to avoid making duplicate API calls
const eventDetailsCache = new Map<
  string,
  EventByIdPartialDetailsQuery["eventById"]
>();

/**
 * This hook is used to merge the event details with the event from the events array
 * - This allows us to return less data in the `Many` call and it avoids
 *   making duplicate API calls for the same event
 * @param events - The events array
 * @returns The selected event and the setSelectedEventId function
 */
export const useEventDetailsDataMerger = (
  events: EventManyQuery["eventMany"],
) => {
  // Store the selected Event
  const [selectedEvent, setSelectedEvent] =
    useState<EventWithOccurrences | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [fetchRemainingEventDetails] = useEventByIdPartialDetailsLazyQuery();

  // When the selectedEventId changes, we need to fetch the event details
  // from the API and merge the result with the event from the events array
  useEffect(() => {
    if (!selectedEventId) {
      setSelectedEvent(null);
      return;
    }
    // Get the event from the events array
    // We'll need this to merge with the event details from the API
    const eventBase = events.find(
      (event: EventManyQuery["eventMany"][0]) => event._id === selectedEventId,
    );
    // Check to see if the event exists in the mergedEventDetails map
    // This will avoid making duplicate API calls for the same event
    const event = eventDetailsCache.get(selectedEventId);
    if (event) {
      setSelectedEvent({
        ...eventBase,
        ...event,
      } as EventWithOccurrences);
      return;
    }
    // Fetch the event details from the API and merge the result with the
    // event from the events array
    const fetchEvent = async () => {
      const response = await fetchRemainingEventDetails({
        variables: {
          id: selectedEventId,
        },
      });
      if (response.data) {
        const event = response.data.eventById;
        eventDetailsCache.set(selectedEventId, event);

        // Merge the event details with the event from the events array
        setSelectedEvent({
          ...eventBase,
          ...event,
        } as EventWithOccurrences);
      }
    };
    fetchEvent();
  }, [events, selectedEventId, fetchRemainingEventDetails]);

  return {
    selectedEvent,
    setSelectedEventId,
  };
};
